/**
 * Filter games based on filter criteria
 * @param {Array} games - Array of game objects
 * @param {Object} filters - Filter criteria
 * @returns {Array} - Filtered games
 */
export const filterGames = (games, filters) => {
  if (!games || !Array.isArray(games)) {
    return [];
  }

  // Make a copy of the games array to avoid modifying the original
  let filteredGames = [...games];

  // Filter by specific game
  if (filters.gameId) {
    filteredGames = filteredGames.filter(game => game.id === filters.gameId);
    return filteredGames; // If filtering by specific game, ignore other filters
  }

  // Filter by date range
  if (filters.dateRange && filters.dateRange !== 'all') {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format

    switch (filters.dateRange) {
      case 'last7':
        // Last 7 days
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];
        filteredGames = filteredGames.filter(game => game.date >= sevenDaysAgoStr && game.date <= todayStr);
        break;
      
      case 'last30':
        // Last 30 days
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split('T')[0];
        filteredGames = filteredGames.filter(game => game.date >= thirtyDaysAgoStr && game.date <= todayStr);
        break;
      
      case 'custom':
        // Custom date range
        if (filters.customDateStart) {
          filteredGames = filteredGames.filter(game => game.date >= filters.customDateStart);
        }
        if (filters.customDateEnd) {
          filteredGames = filteredGames.filter(game => game.date <= filters.customDateEnd);
        }
        break;
      
      case 'season':
        // Current season - this is handled by the season selector, so no additional filtering needed
        break;
      
      default:
        break;
    }
  }

  // Filter by opponent
  if (filters.opponent && filters.opponent !== 'all') {
    filteredGames = filteredGames.filter(game => game.opponent === filters.opponent);
  }

  // Filter by location
  if (filters.location && filters.location !== 'all') {
    if (filters.location === 'home') {
      filteredGames = filteredGames.filter(game => game.isHome === true);
    } else if (filters.location === 'away') {
      filteredGames = filteredGames.filter(game => game.isHome === false);
    } else {
      // Specific location
      filteredGames = filteredGames.filter(game => game.location === filters.location);
    }
  }

  return filteredGames;
};

/**
 * Calculate stats from filtered games
 * @param {Array} filteredGames - Array of filtered game objects
 * @param {Function} calculateStatsFn - Function to calculate stats from games
 * @param {Object} emptyStats - Empty stats object to use if no games
 * @returns {Object} - Calculated stats
 */
export const calculateFilteredStats = (filteredGames, calculateStatsFn, emptyStats) => {
  if (!filteredGames || filteredGames.length === 0) {
    return emptyStats;
  }

  return calculateStatsFn(filteredGames);
};
